import { ref } from "vue";
import type { RouteLocationNormalized } from "vue-router";

// Define the type for a query object
interface QueryObject {
  [key: string]: string | string[] | undefined;
}

// Define the type for the query history entry
type QueryHistoryEntry = string; // Query string (e.g., 'search=foo&sort=asc')
type QueryMap = Map<string, QueryHistoryEntry[]>; // Map of route paths to query arrays

// Shared reactive Map to store query history
const queryMap = ref<QueryMap>(new Map());

export function useQueryHistory() {
  // Add a query to the Map for a given route path
  function addQuery(routePath: string, query: QueryObject): void {
    // Convert query object to a string
    const queryString = new URLSearchParams(
      query as Record<string, string>
    ).toString();
    // Initialize array for routePath if it doesn't exist
    if (!queryMap.value.has(routePath)) {
      queryMap.value.set(routePath, []);
    }
    // Add the query string to the array if it's not a duplicate of the last entry
    const queries = queryMap.value.get(routePath)!;
    if (queries.length === 0 || queries[queries.length - 1] !== queryString) {
      queries.push(queryString);
    }
  }

  // Retrieve the list of queries for a given route path
  function getQueries(routePath: string): QueryHistoryEntry[] {
    return queryMap.value.get(routePath) || [];
  }

  // Clear the query history for a specific route
  function clearQueries(routePath: string): void {
    if (queryMap.value.has(routePath)) {
      queryMap.value.set(routePath, []);
    }
  }

  return { addQuery, getQueries, clearQueries };
}
